/**
 * Auto-layout engine: produces React Flow nodes and edges from a Topology.
 *
 * The layout is deterministic — same topology produces same layout — so
 * user-adjusted positions (stored as layoutOverrides) can be reliably
 * merged in on top.
 *
 * Layout rules (mirroring the v2 prototype):
 *   - Inverters stack vertically on the left
 *   - DC side (isolators, fuses, panels) runs to the right
 *   - AC side (IMS, bus, MSB chain, grid) runs below
 *   - LOADS branch (RCD, MCB) drops from the MSB
 *
 * A4 landscape target: 1123 x 794 pixels at 96 DPI (297mm x 210mm).
 * We aim to keep the diagram within these bounds and auto-scale if needed.
 */
import type { Topology, PanelType, Inverter } from '../types/topology';
import type { SldNode, SldEdge } from '../store/editor';
import { SYMBOLS, renderPanelString } from '../symbols/library';
import { getBrandLogoUrl } from '../catalog/brandLogos';

/**
 * Multi-panel-type helpers.
 *
 * - `getPanelTypes(t)` returns the topology's registered panel types.
 *   When `topology.panelTypes` is missing/empty, derives a single
 *   default type ('default') from the legacy `topology.panels` field
 *   so existing presets continue to work unchanged.
 *
 * - `getInverterPanelType(t, inv)` resolves the panel type for an
 *   inverter — its `panelTypeId` if set + matching, otherwise the
 *   first registered type.
 */
export function getPanelTypes(t: Topology): PanelType[] {
  if (t.panelTypes && t.panelTypes.length > 0) return t.panelTypes;
  if (t.panels) return [{ ...t.panels, id: 'default' }];
  return [];
}

export function getInverterPanelType(t: Topology, inv: Inverter): PanelType | undefined {
  const types = getPanelTypes(t);
  if (inv.panelTypeId) {
    const found = types.find((p) => p.id === inv.panelTypeId);
    if (found) return found;
  }
  return types[0];
}

/**
 * Microinverter detection.
 *
 * WHY a heuristic and not just `inverter.type`: the SP backend
 * (project-topology.php) emits EVERY inverter as type 'string' — it
 * has no per-product micro flag — so a real Enphase IQ8 array arrives
 * tagged 'string'. Detecting micros here lets us collapse the whole
 * fleet into ONE compact AC-branch block instead of one full
 * inverter+DC train per unit (22 IQ8HCs = 22 stacked rows that blow
 * past the page and clip on PDF export).
 *
 * A micro is anything that is EITHER explicitly typed 'microinverter',
 * OR made by a known micro-only brand, OR whose model matches a micro
 * family pattern, OR whose per-unit AC capacity is microinverter-sized
 * (≤1.2 kW — the smallest string inverters start ~1.5 kW, so this is a
 * safe floor that also catches multi-panel micros).
 */
const MICRO_BRANDS = [
  'enphase', 'apsystems', 'ap systems', 'ap-systems',
  'hoymiles', 'nep', 'sparq', 'chilicon', 'involar',
];
const MICRO_MODEL_RE = /(micro|iq\s?[5-9]|hm[st]?-?\d|hms-|hmt-|qs1|qt2|ds3|yc[0-9])/i;
export function isMicroInverter(inv: Inverter): boolean {
  if (inv.type === 'microinverter') return true;
  const brand = (inv.manufacturer ?? '').toLowerCase();
  if (MICRO_BRANDS.some((mb) => brand.includes(mb))) return true;
  if (MICRO_MODEL_RE.test(inv.model ?? '')) return true;
  if (inv.capacity > 0 && inv.capacity <= 1200) return true;
  return false;
}

/** A collapsed group of identical microinverters — one visual block
 *  representing `count` units feeding a shared AC branch. */
interface MicroGroup {
  rep: Inverter;
  count: number;
  totalPanels: number;
}

/** Group consecutive identical micros (same brand/model/capacity/phase/
 *  panel type) into single blocks, summing their panels. Non-consecutive
 *  identicals stay separate so mixed micro arrays still read distinctly. */
function groupMicroInverters(micros: Inverter[]): MicroGroup[] {
  const sig = (i: Inverter) =>
    [i.manufacturer, i.model, i.capacity, i.phases, i.panelTypeId ?? ''].join('::');
  const panelsOf = (i: Inverter) =>
    i.mppts.reduce((s, m) => s + m.strings.reduce((s2, st) => s2 + st.panels, 0), 0) || 1;
  const groups: MicroGroup[] = [];
  for (const mi of micros) {
    const last = groups[groups.length - 1];
    if (last && sig(last.rep) === sig(mi)) {
      last.count += 1;
      last.totalPanels += panelsOf(mi);
    } else {
      groups.push({ rep: mi, count: 1, totalPanels: panelsOf(mi) });
    }
  }
  return groups;
}

// ============================================================
// Layout constants
// ============================================================
const L = {
  leftMargin: 30,
  topMargin: 60,
  invX: 180,            // X of inverter body left edge
  invW: 120,
  invH: 100,
  invVSpacing: 40,      // between stacked inverters
  laneHeight: 78,       // per MPPT string — fits label + rails + panels + margin
  invTopMargin: 20,
  invBottomMargin: 48,

  // DC-side X offsets from inverter right edge
  dxRoofIso: 75,
  dxFuse: 155,
  dxPanels: 220,

  panelSize: 16,
  panelGap: 3,

  // AC side (computed dynamically based on content). These offsets are
  // kept tight so the AC chain ends well clear of the bottom title block
  // (~20px of label hangs below each symbol — easy to overshoot).
  busYOffset: 56,       // bus below inverter block
  msbYOffset: 44,       // MSB below bus
  loadsYOffset: 110
};

interface LayoutCtx {
  nodes: SldNode[];
  edges: SldEdge[];
  /** auto-increment for unique instance ids */
  nextId: number;
}

function newId(ctx: LayoutCtx, prefix: string): string {
  return `${prefix}-${ctx.nextId++}`;
}

/**
 * Helper: add a symbol node and return its id.
 * `labelOptions` goes into node data for display alongside the symbol.
 */
function addNode(
  ctx: LayoutCtx,
  symbolId: string,
  x: number,
  y: number,
  extra: Partial<SldNode['data']> = {},
  rotation: 0 | 90 | 180 | 270 = 0
): string {
  const sym = SYMBOLS[symbolId];
  if (!sym) throw new Error(`Unknown symbol: ${symbolId}`);
  const id = newId(ctx, symbolId);
  ctx.nodes.push({
    id,
    type: 'sldSymbol',
    position: { x, y },
    data: { symbolId, rotation, ...extra }
  });
  return id;
}

/**
 * Helper: add an edge between two symbols' terminals.
 */
function addEdge(
  ctx: LayoutCtx,
  sourceId: string,
  sourceTerm: string,
  targetId: string,
  targetTerm: string,
  style: 'dc+' | 'dc-' | 'dc' | 'ac' | 'earth' | 'any' = 'any'
): void {
  ctx.edges.push({
    id: `e-${ctx.nextId++}`,
    source: sourceId,
    sourceHandle: sourceTerm,
    target: targetId,
    targetHandle: targetTerm,
    type: 'sldWire',
    data: { style }
  });
}

/**
 * Escape text for safe inclusion inside an SVG <text> element.
 * Just the four critical entities — keeps things lightweight, no
 * full HTML escape needed for SVG text nodes.
 */
function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Approximate the pixel width of an Arial string at a given font
 * size, so we can truncate BOM cell text that would otherwise
 * overflow into the next column. SVG `<text>` has no native
 * `overflow:hidden` / `text-overflow:ellipsis` — long manufacturer
 * names used to bleed into the Model column and obscure it. We
 * walk the string once with four width buckets (narrow / wide /
 * punctuation / default) which gets us close enough without
 * needing a canvas measurement pass at render time.
 */
function estimateTextWidth(s: string, fontSize: number): number {
  let w = 0;
  for (const c of s) {
    if ("ijlt|.,:;'\"!".includes(c)) w += 0.30;
    else if ('WMmw'.includes(c)) w += 0.85;
    else if ('fr -()/'.includes(c)) w += 0.45;
    else w += 0.55;
  }
  return w * fontSize;
}

/**
 * Truncate `s` so its estimated rendered width fits within `maxPx`,
 * appending an ellipsis when truncation happens. Falls back to the
 * original string when it already fits. Used by the title-block
 * BOM cell renderers to keep long values from overlapping the
 * next column.
 */
function truncateToWidth(s: string, maxPx: number, fontSize: number): string {
  if (maxPx <= 0) return '';
  if (estimateTextWidth(s, fontSize) <= maxPx) return s;
  const ellipsis = '\u2026';
  const ellipsisW = estimateTextWidth(ellipsis, fontSize);
  let out = '';
  let w = 0;
  for (const c of s) {
    const cw = estimateTextWidth(c, fontSize);
    if (w + cw + ellipsisW > maxPx) break;
    out += c;
    w += cw;
  }
  return out.replace(/\s+$/u, '') + ellipsis;
}

/**
 * Brand colour palette — matches PropertiesPanel's BrandBadge so a
 * model's badge looks the same in the right-side editor cards as it
 * does in the title-block tables. Add new manufacturers here as the
 * catalogue grows; unknown brands fall back to a hashed colour from
 * `hashBrandColour`.
 */
const BRAND_COLOURS_TB: Record<string, string> = {
  sungrow:        '#dc2626',
  sma:            '#0ea5e9',
  fronius:        '#f97316',
  goodwe:         '#0891b2',
  solaredge:      '#dc2626',
  huawei:         '#dc2626',
  tesla:          '#0f172a',
  enphase:        '#16a34a',
  'jinko solar':  '#1e40af',
  'longi solar':  '#7c3aed',
  'ja solar':     '#dc2626',
  'trina solar':  '#dc2626',
  'canadian solar': '#dc2626',
  rec:            '#16a34a',
  sunpower:       '#1e40af',
  'alpha-ess':    '#7c3aed',
  sonnen:         '#f59e0b',
  byd:            '#dc2626',
  pylontech:      '#0891b2',
};
function hashBrandColour(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  const palette = ['#0f172a', '#1e40af', '#dc2626', '#7c3aed', '#0891b2', '#16a34a', '#f59e0b', '#db2777'];
  return palette[Math.abs(h) % palette.length];
}
function brandInitials(name: string): string {
  const words = name.replace(/[-_]/g, ' ').split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) {
    return words[0].slice(0, words[0].length <= 3 ? words[0].length : 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * SVG snippet for a small brand badge.
 *
 * Two-tier fallback:
 *   1. If a real product logo is registered for this manufacturer
 *      (loaded from tbl_logos via /sld-api/brand-logos.php), render
 *      it as an <image> clipped to a rounded rect of the requested
 *      size. White background so dark and pale logos both read.
 *   2. Otherwise render the coloured rounded square + initials
 *      (matches PropertiesPanel's BrandBadge component) — the
 *      "always-works" fallback for brands not yet in tbl_logos.
 *
 * The `kind` arg lets us look up the right tbl_logos product_type
 * (panels / inverters / batteries) when several have the same brand
 * (e.g. Tesla makes both inverters and batteries).
 */
function brandBadgeSvg(
  manufacturer: string,
  x: number,
  y: number,
  size: number,
  kind?: 'panels' | 'inverters' | 'batteries',
): string {
  // Tier 1: real product logo.
  const logoUrl = kind ? getBrandLogoUrl(kind, manufacturer) : undefined;
  if (logoUrl) {
    // Unique clip-path id per call site — using x,y,size keeps it
    // stable enough for one render but unique across multiple
    // badges on the same page.
    const clipId = `bb-clip-${kind}-${Math.round(x)}-${Math.round(y)}-${size}`;
    const r = size * 0.22;
    return (
      `<defs>` +
      `<clipPath id="${clipId}">` +
      `<rect x="${x}" y="${y}" width="${size}" height="${size}" rx="${r}"/>` +
      `</clipPath>` +
      `</defs>` +
      // White backdrop so transparent / dark logos still read.
      `<rect x="${x}" y="${y}" width="${size}" height="${size}" rx="${r}" fill="#ffffff" stroke="#e2e8f0" stroke-width="0.5"/>` +
      `<image x="${x}" y="${y}" width="${size}" height="${size}" ` +
      `href="${escapeXml(logoUrl)}" preserveAspectRatio="xMidYMid meet" ` +
      `clip-path="url(#${clipId})"/>`
    );
  }

  // Tier 2: initials fallback.
  const key = (manufacturer || '').toLowerCase();
  const colour = BRAND_COLOURS_TB[key] ?? hashBrandColour(key);
  const initials = brandInitials(manufacturer || '?');
  const fontSize = initials.length >= 3 ? size * 0.34 : size * 0.46;
  const cx = x + size / 2;
  const cy = y + size / 2 + fontSize * 0.36;
  return (
    `<rect x="${x}" y="${y}" width="${size}" height="${size}" rx="${size * 0.22}" fill="${colour}"/>` +
    `<text x="${cx}" y="${cy}" font-size="${fontSize.toFixed(1)}" font-weight="700" fill="#ffffff" text-anchor="middle" font-family="system-ui, Arial, sans-serif" letter-spacing="0.4">${escapeXml(initials)}</text>`
  );
}

/**
 * Stacked-battery glyph — used when N identical AC-coupled batteries
 * collapse to one row. Renders 1 or 2 ghost copies of the battery
 * silhouette behind the front face, offset down-right, so the viewer
 * reads "stack" before they read the "× N" label. Sized to match the
 * 56×28 native box of the standard battery symbol so terminals stay
 * at their declared positions (pos at x:0, neg at x:56 — middle row).
 */
function buildStackedBatteryContent(stack: number): string {
  // Cap shadow count at 2 — more would push out of the native bounds
  // and complicate terminal alignment. The "× N" label tells the
  // exact count anyway.
  const shadows = Math.min(stack - 1, 2);
  const stroke = '#0f172a';
  const fill = '#fafbfc';
  let out = '';
  // Ghost copies behind, lighter and offset
  for (let i = shadows; i >= 1; i--) {
    const dx = i * 3;
    const dy = i * 3;
    const opacity = 0.25 + (shadows - i) * 0.15;
    out += `<rect x="${dx}" y="${dy}" width="56" height="28" rx="2.5" ` +
      `fill="${fill}" stroke="${stroke}" stroke-width="1.2" ` +
      `opacity="${opacity}"/>`;
  }
  // Front face — same drawing as the static battery symbol so the
  // top sheet looks identical to a single battery.
  out += `
    <rect x="0" y="0" width="56" height="28" rx="2.5" fill="${fill}" stroke="${stroke}" stroke-width="1.2"/>
    <line x1="6" y1="5" x2="6" y2="23" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="11.5" y1="9" x2="11.5" y2="19" stroke="${stroke}" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="17" y1="5" x2="17" y2="23" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="22.5" y1="9" x2="22.5" y2="19" stroke="${stroke}" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="28" y1="5" x2="28" y2="23" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="33.5" y1="9" x2="33.5" y2="19" stroke="${stroke}" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="39" y1="5" x2="39" y2="23" stroke="${stroke}" stroke-width="1.6" stroke-linecap="round"/>
    <line x1="44.5" y1="9" x2="44.5" y2="19" stroke="${stroke}" stroke-width="1.2" stroke-linecap="round"/>
    <text x="4" y="7" font-size="6" fill="#c62828" font-weight="700" font-family="Arial">+</text>
    <text x="52" y="7" font-size="6" fill="${stroke}" font-weight="700" text-anchor="end" font-family="Arial">−</text>
  `;
  return out;
}

/**
 * Standard SLD title block — sits at the bottom-right of the page,
 * shows project metadata + drawing identification. Reads from
 * topology.project, so it stays in sync as the user edits the project
 * fields. Not connectable (no terminals), draggable but not selectable
 * (it's chrome rather than user content).
 *
 * Drawing number / revision is hard-coded for now ("SLD-001 / Rev A");
 * once we have multi-document support those can become topology fields.
 */
export function makeTitleBlockNode(t: Topology): SldNode {
  const PAPER_W = 1123;
  const PAPER_H = 794;
  // Bottom margin (M_BOT) is intentionally smaller than side/top margin so
  // the title block sits CLOSER to the page edge — this maximises the
  // vertical breathing room between the AC chain (with its labels that
  // hang ~20px below each symbol) and the top of the title block.
  const M_SIDE = 24;
  const M_BOT  = 12;
  const W = PAPER_W - 2 * M_SIDE;     // 1075
  // Outer border + section dividers — bumped from 1.2 → 1.8 so the
  // table boundaries read as a proper drafting frame, not a hairline
  // grid. Sub-cells (column / row dividers inside a table) stay at
  // 0.4–0.5 for visual hierarchy.
  const STROKE_BOLD = 1.8;
  const STROKE_DIV = 0.5;
  const STROKE_FAINT = 0.4;

  // ----- Aggregate stats -----
  let totalPanels = 0;
  for (const inv of t.inverters) {
    for (const m of inv.mppts) {
      for (const s of m.strings) totalPanels += s.panels;
    }
  }
  // Group inverters by manufacturer+model so identical units render as
  // one row (e.g. 3x SG33CX → "3 | Sungrow | SG33CX | ...").
  type InvGroup = { count: number; inv: Topology['inverters'][number] };
  const invGroupMap = new Map<string, InvGroup>();
  for (const inv of t.inverters) {
    const key = `${inv.manufacturer}|${inv.model}`;
    const cur = invGroupMap.get(key);
    if (cur) cur.count++;
    else invGroupMap.set(key, { count: 1, inv });
  }
  const invGroups = Array.from(invGroupMap.values());

  // Group batteries by manufacturer+model+coupling+capacity so
  // identical units render as one row (4× IQ Battery 5P → 1 row
  // with qty 4). Same idea as the inverter groups.
  type BatGroup = { count: number; bat: Topology['batteries'][number] };
  const batGroupMap = new Map<string, BatGroup>();
  for (const bat of t.batteries) {
    const key = `${bat.manufacturer}|${bat.model}|${bat.coupling}|${bat.capacity}`;
    const cur = batGroupMap.get(key);
    if (cur) cur.count++;
    else batGroupMap.set(key, { count: 1, bat });
  }
  const batGroups = Array.from(batGroupMap.values());
  const hasBatteries = batGroups.length > 0;

  // ----- Layout proportions -----
  // New layout (v3):
  //   ┌───────────────┬────────┬────────────────────────────────────┐
  //   │ Project info  │ PV     │ Qty Mfr Model Pmax Voc Isc Fuse    │
  //   │ + installer   │ MODULES│ ...                                │
  //   │               │────────│────────────────────────────────────│
  //   │               │ INVRTR │ Qty Mfr Model AC Phases MPPTs Spec │
  //   │               │        │ ...                                │
  //   │               │────────│────────────────────────────────────│
  //   │               │ BATTRY │ Qty Mfr Model Cap Coupling AC Bkp  │
  //   └───────────────┴────────┴────────────────────────────────────┘
  // Section title bands sit to the LEFT of each table, not as a
  // header strip above. That kills the per-section header strip
  // entirely (saves ~20px per section) so the whole block stays on
  // the page. Min H is pinned to LEFT_MIN_H so the project info
  // never overflows when battery section is removed.
  const LEFT_MIN_H = 132;        // address (40) + date (40) + installer (52)
  const SECTION_ROW_H = 38;      // col-header row (14) + data row (24)
  const BANNER_H = 22;           // top headline strip with kWp / kW AC / kWh totals
  const sectionsCount = hasBatteries ? 3 : 2;
  const rightTotalH = sectionsCount * SECTION_ROW_H;
  const bodyH = Math.max(LEFT_MIN_H, rightTotalH);
  const H = BANNER_H + bodyH;
  const x = M_SIDE;
  const y = PAPER_H - H - M_BOT;

  const leftColW = 320;
  const labelColW = 70;          // vertical band holding section titles
  const labelColX = leftColW;
  const rightColX = leftColW + labelColW;
  const rightColW = W - rightColX;

  // Section Y offsets — distribute SECTION_ROW_H slots from the top
  // of the BODY (below the banner). Sections always span the full
  // body height; the banner sits above them spanning full width.
  const sectionH = bodyH / sectionsCount;
  const pvSectionY = BANNER_H;
  const invSectionY = BANNER_H + sectionH;
  const batSectionY = BANNER_H + sectionH * 2;
  const COL_HEADER_H = 14;       // top sliver of each section row

  // ----- System sizing totals (used in the headline banner) -----
  const totalKwDc = (() => {
    const pvTypes = getPanelTypes(t);
    const defaultId = pvTypes[0]?.id;
    let kw = 0;
    for (const inv of t.inverters) {
      const tid = inv.panelTypeId ?? defaultId;
      const pt = pvTypes.find((p) => p.id === tid);
      if (!pt) continue;
      const panels = inv.mppts.reduce((s, m) => s + m.strings.reduce((s2, str) => s2 + str.panels, 0), 0);
      kw += (panels * pt.pmax) / 1000;
    }
    return kw;
  })();
  const totalKwAc = t.inverters.reduce((s, i) => s + i.capacity, 0) / 1000;
  const totalKwh  = t.batteries.reduce((s, b) => s + b.capacity, 0);
  const totalPanelsCount = t.inverters.reduce(
    (s, i) => s + i.mppts.reduce((s2, m) => s2 + m.strings.reduce((s3, str) => s3 + str.panels, 0), 0),
    0,
  );

  const parts: string[] = [];
  const addr = t.project?.address ?? '';
  const date = t.project?.installDate ?? '';
  const inst = t.project?.installer ?? { name: '', address: '', phone: '' };

  // Outer border + main vertical dividers (LEFT | LABEL | RIGHT)
  // span the full height including the banner.
  parts.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff" stroke="#0f172a" stroke-width="${STROKE_BOLD}"/>`);
  parts.push(`<line x1="${leftColW}" y1="${BANNER_H}" x2="${leftColW}" y2="${H}" stroke="#0f172a" stroke-width="${STROKE_BOLD}"/>`);
  parts.push(`<line x1="${rightColX}" y1="${BANNER_H}" x2="${rightColX}" y2="${H}" stroke="#0f172a" stroke-width="${STROKE_BOLD}"/>`);
  // Bold horizontal divider between the banner and the body.
  parts.push(`<line x1="0" y1="${BANNER_H}" x2="${W}" y2="${BANNER_H}" stroke="#0f172a" stroke-width="${STROKE_BOLD}"/>`);
  // Horizontal section dividers between PV / INV / BAT (span the
  // label band + the right tabular area).
  parts.push(`<line x1="${labelColX}" y1="${invSectionY}" x2="${W}" y2="${invSectionY}" stroke="#0f172a" stroke-width="${STROKE_BOLD}"/>`);
  if (hasBatteries) {
    parts.push(`<line x1="${labelColX}" y1="${batSectionY}" x2="${W}" y2="${batSectionY}" stroke="#0f172a" stroke-width="${STROKE_BOLD}"/>`);
  }

  // ===== TOP BANNER: system sizing headline =====
  // Quick-scan totals — kWp DC (panels × pmax sum), kW AC (sum of
  // inverter capacities), kWh storage (sum of battery capacities),
  // total panel count. Skips empty bits gracefully (battery-only,
  // solar-only, etc).
  parts.push(`<rect x="0" y="0" width="${W}" height="${BANNER_H}" fill="#0f172a"/>`);
  // Drawing # / Rev now lives in the right side of the banner — a
  // natural place for "what is this drawing" metadata next to the
  // "what is this system" totals on the left.
  const drawingNumber = (t.project?.drawingNumber ?? 'SLD-001').slice(0, 18);
  const revision      = (t.project?.revision      ?? 'A').slice(0, 6);
  parts.push(`<text x="${W - 10}" y="14.5" font-size="9.5" fill="#cbd5e1" text-anchor="end" font-family="Arial">Drawing <tspan font-weight="700" fill="#ffffff">${escapeXml(drawingNumber)}</tspan> · Rev <tspan font-weight="700" fill="#ffffff">${escapeXml(revision)}</tspan></text>`);
  // Sizing summary on the left of the banner.
  const sizingParts: string[] = [];
  if (totalKwDc > 0) sizingParts.push(`<tspan font-weight="700" fill="#ffffff">${totalKwDc.toFixed(2)} kWp</tspan> <tspan fill="#94a3b8">DC</tspan>`);
  if (totalKwAc > 0) sizingParts.push(`<tspan font-weight="700" fill="#ffffff">${totalKwAc.toFixed(2)} kW</tspan> <tspan fill="#94a3b8">AC</tspan>`);
  if (totalKwh > 0)  sizingParts.push(`<tspan font-weight="700" fill="#ffffff">${totalKwh.toFixed(1)} kWh</tspan> <tspan fill="#94a3b8">storage</tspan>`);
  if (totalPanelsCount > 0) sizingParts.push(`<tspan font-weight="700" fill="#ffffff">${totalPanelsCount}</tspan> <tspan fill="#94a3b8">panels</tspan>`);
  parts.push(
    `<text x="10" y="14.5" font-size="11" fill="#cbd5e1" font-family="Arial">` +
    `<tspan font-weight="700" fill="#ffffff" letter-spacing="0.5">SYSTEM</tspan>  ` +
    sizingParts.join(`<tspan fill="#475569"> · </tspan>`) +
    `</text>`
  );

  // ===== LEFT: project info + installer logo =====
  // All Y offsets shift down by BANNER_H so they sit below the
  // banner. LEFT body height is bodyH (= H - BANNER_H).
  const bL = BANNER_H;            // banner-aware base Y for LEFT column
  parts.push(`<line x1="0" y1="${bL + 50}" x2="${leftColW}" y2="${bL + 50}" stroke="#cbd5e1" stroke-width="0.7"/>`);
  parts.push(`<line x1="0" y1="${bL + 92}" x2="${leftColW}" y2="${bL + 92}" stroke="#cbd5e1" stroke-width="0.7"/>`);

  parts.push(`<text x="10" y="${bL + 20}" font-size="11" fill="#0f172a" font-weight="700" font-family="Arial">Installation Address:</text>`);
  parts.push(`<text x="10" y="${bL + 38}" font-size="10" fill="#0f172a" font-family="Arial">${escapeXml(addr.slice(0, 50))}</text>`);

  parts.push(`<text x="10" y="${bL + 65}" font-size="11" fill="#0f172a" font-weight="700" font-family="Arial">Installation Date:</text>`);
  parts.push(`<text x="10" y="${bL + 83}" font-size="10" fill="#0f172a" font-family="Arial">${escapeXml(date)}</text>`);

  if (inst.logoUrl) {
    parts.push(`<image x="10" y="${bL + 98}" width="50" height="30" href="${escapeXml(inst.logoUrl)}" preserveAspectRatio="xMidYMid meet"/>`);
  } else {
    parts.push(`<rect x="10" y="${bL + 98}" width="50" height="30" fill="#1e293b" stroke="#475569" stroke-width="0.5" rx="3"/>`);
    parts.push(`<text x="35" y="${bL + 116}" font-size="8" fill="#94a3b8" text-anchor="middle" font-weight="700" font-family="Arial">LOGO</text>`);
  }

  parts.push(`<text x="68" y="${bL + 107}" font-size="10" fill="#0f172a" font-weight="700" font-family="Arial">${escapeXml((inst.name ?? '').slice(0, 32))}</text>`);
  parts.push(`<text x="68" y="${bL + 119}" font-size="8.5" fill="#475569" font-family="Arial">${escapeXml((inst.address ?? '').slice(0, 36))}</text>`);
  parts.push(`<text x="68" y="${bL + 129}" font-size="8.5" fill="#475569" font-family="Arial">${escapeXml(inst.phone ?? '')}</text>`);

  // ===== Helper: render a section title band on the LEFT of a row =====
  // Renders the section name in two stacked lines (e.g. "PV" / "MODULES")
  // centred horizontally + vertically in the labelColW band. Background
  // tint matches the old header strip. Returns nothing — pushes to parts.
  function renderSectionLabel(yTop: number, height: number, line1: string, line2?: string) {
    parts.push(`<rect x="${labelColX}" y="${yTop}" width="${labelColW}" height="${height}" fill="#f1f5f9"/>`);
    const cx = labelColX + labelColW / 2;
    const cy = yTop + height / 2;
    if (line2) {
      parts.push(`<text x="${cx}" y="${cy - 1}" font-size="9.5" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="Arial">${line1}</text>`);
      parts.push(`<text x="${cx}" y="${cy + 11}" font-size="9.5" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="Arial">${line2}</text>`);
    } else {
      parts.push(`<text x="${cx}" y="${cy + 4}" font-size="9.5" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="Arial">${line1}</text>`);
    }
  }

  // ===== PV MODULES section =====
  renderSectionLabel(pvSectionY, sectionH, 'PV', 'MODULES');
  const pvColHdrEnd = pvSectionY + COL_HEADER_H;

  const pvCols = [
    { label: 'Qty',          rel: 30  },
    { label: 'Manufacturer', rel: 200 },
    { label: 'Model',        rel: 160 },
    { label: 'P_MAX',        rel: 50  },
    { label: 'V_OC',         rel: 50  },
    { label: 'I_SC',         rel: 50  },
    { label: 'Fuse',         rel: 40  }
  ];
  const pvRelTotal = pvCols.reduce((a, c) => a + c.rel, 0);
  const pvColW = pvCols.map(c => (c.rel / pvRelTotal) * rightColW);
  const pvColX: number[] = [];
  { let cx = 0; for (const w of pvColW) { pvColX.push(cx); cx += w; } }

  pvCols.forEach((c, i) => {
    if (i > 0) parts.push(`<line x1="${rightColX + pvColX[i]}" y1="${pvSectionY}" x2="${rightColX + pvColX[i]}" y2="${pvSectionY + sectionH}" stroke="#cbd5e1" stroke-width="${STROKE_DIV}"/>`);
    parts.push(`<text x="${rightColX + pvColX[i] + 5}" y="${pvSectionY + 10}" font-size="8.5" font-weight="600" fill="#475569" font-family="Arial">${c.label}</text>`);
  });
  parts.push(`<line x1="${rightColX}" y1="${pvColHdrEnd}" x2="${W}" y2="${pvColHdrEnd}" stroke="#cbd5e1" stroke-width="${STROKE_DIV}"/>`);

  const pvTypes = getPanelTypes(t);
  const pvTypeRows = pvTypes
    .map((pt) => {
      const defaultId = pvTypes[0]?.id;
      const qty = t.inverters
        .filter((i) => (i.panelTypeId ?? defaultId) === pt.id)
        .reduce((sum, i) =>
          sum + i.mppts.reduce((s, m) =>
            s + m.strings.reduce((s2, str) => s2 + str.panels, 0), 0), 0);
      return { pt, qty };
    })
    .filter((r) => r.qty > 0);
  const pvRowHAvail = sectionH - COL_HEADER_H;
  const pvRowH = pvRowHAvail / Math.max(pvTypeRows.length, 1);
  pvTypeRows.forEach((row, idx) => {
    const rowY = pvColHdrEnd + idx * pvRowH;
    if (idx > 0) parts.push(`<line x1="${rightColX}" y1="${rowY}" x2="${W}" y2="${rowY}" stroke="#cbd5e1" stroke-width="${STROKE_FAINT}"/>`);
    const textY = rowY + pvRowH / 2 + 3;
    const existingTag = row.pt.isExisting ? ' (EXISTING)' : '';
    const data = [
      String(row.qty),
      (row.pt.manufacturer ?? '') + existingTag,
      row.pt.model ?? '',
      `${row.pt.pmax}W`,
      `${row.pt.voc}V`,
      `${row.pt.isc}A`,
      `${row.pt.fuseRating}A`
    ];
    // Manufacturer column gets a small inline brand badge (coloured
    // square with initials) so the BOM scans visually — same colour
    // as the BrandBadge in the right-side editor.
    const badgeSize = Math.min(pvRowH - 4, 14);
    const badgeY = rowY + (pvRowH - badgeSize) / 2;
    parts.push(brandBadgeSvg(
      row.pt.manufacturer ?? '?',
      rightColX + pvColX[1] + 4,
      badgeY,
      badgeSize,
      'panels',
    ));
    data.forEach((d, i) => {
      // Manufacturer cell text starts after the badge; other cells
      // get the standard 5px left padding.
      const xOff = i === 1 ? badgeSize + 8 : 5;
      // Truncate so a long manufacturer name (or any value) can't
      // bleed into the next column. The full string is kept as a
      // <title> child so SVG-aware viewers show it on hover.
      const cellW = Math.max(0, pvColW[i] - xOff - 5);
      const raw = String(d);
      const shown = truncateToWidth(raw, cellW, 9);
      const tip = shown === raw ? '' : `<title>${escapeXml(raw)}</title>`;
      parts.push(`<text x="${rightColX + pvColX[i] + xOff}" y="${textY}" font-size="9" fill="${row.pt.isExisting ? '#7c3aed' : '#0f172a'}" font-family="Arial">${escapeXml(shown)}${tip}</text>`);
    });
  });

  // ===== INVERTERS section =====
  renderSectionLabel(invSectionY, sectionH, 'INVERTERS');
  const invColHdrEnd = invSectionY + COL_HEADER_H;

  const invCols = [
    { label: 'Qty',          rel: 30  },
    { label: 'Manufacturer', rel: 200 },
    { label: 'Model',        rel: 130 },
    { label: 'AC Power',     rel: 75  },
    { label: 'Phases',       rel: 35  },
    { label: 'MPPTs',        rel: 35  },
    { label: 'MPPT Spec',    rel: 115 },
  ];
  const invRelTotal = invCols.reduce((a, c) => a + c.rel, 0);
  const invColW = invCols.map(c => (c.rel / invRelTotal) * rightColW);
  const invColX: number[] = [];
  { let cx = 0; for (const w of invColW) { invColX.push(cx); cx += w; } }

  invCols.forEach((c, i) => {
    if (i > 0) parts.push(`<line x1="${rightColX + invColX[i]}" y1="${invSectionY}" x2="${rightColX + invColX[i]}" y2="${invSectionY + sectionH}" stroke="#cbd5e1" stroke-width="${STROKE_DIV}"/>`);
    parts.push(`<text x="${rightColX + invColX[i] + 5}" y="${invSectionY + 10}" font-size="8.5" font-weight="600" fill="#475569" font-family="Arial">${c.label}</text>`);
  });
  parts.push(`<line x1="${rightColX}" y1="${invColHdrEnd}" x2="${W}" y2="${invColHdrEnd}" stroke="#cbd5e1" stroke-width="${STROKE_DIV}"/>`);

  const invRowH = (sectionH - COL_HEADER_H) / Math.max(invGroups.length, 1);
  invGroups.forEach((g, rowIdx) => {
    const rowY = invColHdrEnd + rowIdx * invRowH;
    if (rowIdx > 0) parts.push(`<line x1="${rightColX}" y1="${rowY}" x2="${W}" y2="${rowY}" stroke="#cbd5e1" stroke-width="${STROKE_FAINT}"/>`);
    const inv = g.inv;
    const dataMid = rowY + invRowH / 2 + 3;
    const invExistingTag = inv.isExisting ? ' (EXISTING)' : '';
    const invFill = inv.isExisting ? '#7c3aed' : '#0f172a';
    const mp = inv.mppts[0];
    const mpptSpec = mp
      ? `${mp.maxCurrent}A · ${mp.voltageRange[0]}–${mp.voltageRange[1]}V`
      : '—';
    const data = [
      String(g.count),
      (inv.manufacturer ?? '') + invExistingTag,
      inv.model ?? '',
      `${(inv.capacity / 1000).toFixed(1)} kW`,
      `${inv.phases}φ`,
      String(inv.mppts.length),
      mpptSpec,
    ];
    const invBadgeSize = Math.min(invRowH - 4, 14);
    const invBadgeY = rowY + (invRowH - invBadgeSize) / 2;
    parts.push(brandBadgeSvg(
      inv.manufacturer ?? '?',
      rightColX + invColX[1] + 4,
      invBadgeY,
      invBadgeSize,
      'inverters',
    ));
    data.forEach((d, i) => {
      const xOff = i === 1 ? invBadgeSize + 8 : 5;
      const cellW = Math.max(0, invColW[i] - xOff - 5);
      const raw = String(d);
      const shown = truncateToWidth(raw, cellW, 9);
      const tip = shown === raw ? '' : `<title>${escapeXml(raw)}</title>`;
      parts.push(`<text x="${rightColX + invColX[i] + xOff}" y="${dataMid}" font-size="9" fill="${invFill}" font-family="Arial">${escapeXml(shown)}${tip}</text>`);
    });
  });

  // ===== BATTERIES section (only when batteries exist) =====
  if (hasBatteries) {
    renderSectionLabel(batSectionY, sectionH, 'BATTERIES');
    const batColHdrEnd = batSectionY + COL_HEADER_H;

    const batCols = [
      { label: 'Qty',          rel: 30  },
      { label: 'Manufacturer', rel: 180 },
      { label: 'Model',        rel: 130 },
      { label: 'Capacity',     rel: 60  },
      { label: 'Coupling',     rel: 40  },
      { label: 'AC Inverter',  rel: 100 },
      { label: 'Backup',       rel: 40  },
    ];
    const batRelTotal = batCols.reduce((a, c) => a + c.rel, 0);
    const batColW = batCols.map(c => (c.rel / batRelTotal) * rightColW);
    const batColX: number[] = [];
    { let cx = 0; for (const w of batColW) { batColX.push(cx); cx += w; } }

    batCols.forEach((c, i) => {
      if (i > 0) parts.push(`<line x1="${rightColX + batColX[i]}" y1="${batSectionY}" x2="${rightColX + batColX[i]}" y2="${batSectionY + sectionH}" stroke="#cbd5e1" stroke-width="${STROKE_DIV}"/>`);
      parts.push(`<text x="${rightColX + batColX[i] + 5}" y="${batSectionY + 10}" font-size="8.5" font-weight="600" fill="#475569" font-family="Arial">${c.label}</text>`);
    });
    parts.push(`<line x1="${rightColX}" y1="${batColHdrEnd}" x2="${W}" y2="${batColHdrEnd}" stroke="#cbd5e1" stroke-width="${STROKE_DIV}"/>`);

    const batRowH = (sectionH - COL_HEADER_H) / Math.max(batGroups.length, 1);
    batGroups.forEach((g, idx) => {
      const rowY = batColHdrEnd + idx * batRowH;
      if (idx > 0) parts.push(`<line x1="${rightColX}" y1="${rowY}" x2="${W}" y2="${rowY}" stroke="#cbd5e1" stroke-width="${STROKE_FAINT}"/>`);
      const textY = rowY + batRowH / 2 + 3;
      const bat = g.bat;
      const isExisting = bat.isExisting === true;
      const fill = isExisting ? '#7c3aed' : '#0f172a';
      const existingTag = isExisting ? ' (EXISTING)' : '';
      const acInverter = bat.coupling === 'ac' && bat.inverter
        ? `${(bat.inverter.capacity / 1000).toFixed(1)} kW · ${bat.inverter.phases}φ`
        : bat.coupling === 'dc'
          ? `via ${bat.connectedTo ?? 'hybrid'}`
          : '—';
      const data = [
        String(g.count),
        (bat.manufacturer ?? '') + existingTag,
        bat.model ?? '',
        `${bat.capacity} kWh`,
        bat.coupling.toUpperCase(),
        acInverter,
        bat.backupGateway ? 'Yes' : '—',
      ];
      const batBadgeSize = Math.min(batRowH - 4, 14);
      const batBadgeY = rowY + (batRowH - batBadgeSize) / 2;
      parts.push(brandBadgeSvg(
        bat.manufacturer ?? '?',
        rightColX + batColX[1] + 4,
        batBadgeY,
        batBadgeSize,
        'batteries',
      ));
      data.forEach((d, i) => {
        const xOff = i === 1 ? batBadgeSize + 8 : 5;
        const cellW = Math.max(0, batColW[i] - xOff - 5);
        const raw = String(d);
        const shown = truncateToWidth(raw, cellW, 9);
        const tip = shown === raw ? '' : `<title>${escapeXml(raw)}</title>`;
        parts.push(`<text x="${rightColX + batColX[i] + xOff}" y="${textY}" font-size="9" fill="${fill}" font-family="Arial">${escapeXml(shown)}${tip}</text>`);
      });
    });
  }

  return {
    id: '__title_block__',
    type: 'sldSymbol',
    position: { x, y },
    data: {
      symbolId: 'panelString',
      customContent: parts.join('\n'),
      customBox: { w: W, h: H },
      customTerminals: {}
    },
    selectable: false,
    draggable: true
  } as SldNode;
}

/**
 * Main entry: produce nodes + edges from a topology.
 */
export function autoLayout(t: Topology): { nodes: SldNode[]; edges: SldEdge[] } {
  const ctx: LayoutCtx = { nodes: [], edges: [], nextId: 1 };

  // ----- Adaptive vertical spacing -----
  // Default L values are tuned for 1-3 inverter blocks; with 4+ inverters
  // the natural stack height exceeds A4 landscape (794px). We mutate L's
  // properties for the duration of this call so a 5-inverter, 2-lane-each
  // commercial preset still fits on the page. The mutation is safe because
  // autoLayout is synchronous and there are no concurrent callers — we
  // restore the originals via the try/finally below.
  // Split string/hybrid inverters from microinverters. Micros collapse
  // into one AC-branch block per identical group (see groupMicroInverters)
  // rather than one full inverter+DC train each, so a 22-unit IQ8 array
  // becomes a single compact block instead of 22 page-blowing rows.
  const macroInverters = t.inverters.filter((i) => !isMicroInverter(i));
  const microGroups    = groupMicroInverters(t.inverters.filter(isMicroInverter));

  // "Tall" spacing compression is driven by the number of stacked
  // BLOCKS, not raw inverter count — after micro collapse a big micro
  // job is only a couple of blocks and shouldn't be squashed.
  const blockCount = macroInverters.length + microGroups.length
    + t.batteries.filter(b => b.coupling === 'ac').length;
  const isTall = blockCount >= 4;
  const savedL = { ...L };
  if (isTall) {
    L.topMargin    = 30;
    L.invH         = 80;
    L.invVSpacing  = 16;
    L.laneHeight   = 60;
    L.busYOffset   = 44;
    L.msbYOffset   = 44;
  }
  try {

  // ----- Pass 1: compute vertical positions for each inverter -----
  let cursorY = L.topMargin;
  const invLayouts: Array<{
    inv?: Topology['inverters'][number];
    battery?: Topology['batteries'][number];
    /** Collapsed microinverter fleet rendered as one AC-branch block. */
    microGroup?: MicroGroup;
    /** When this lane represents N identical AC-coupled batteries
     *  collapsed into a single visual row (Enphase IQ 5P stacks etc),
     *  this is N. Undefined / 1 means a standalone battery. */
    batteryStackCount?: number;
    y: number;
    h: number;
    // collapsedCount = N when this lane represents N parallel strings
    // collapsed into a single visual row (because all strings on the
    // MPPT have the same panel count). undefined / 1 means it's a
    // standalone string drawn on its own lane.
    lanes: Array<{
      mppt: number;
      stringIdx: number;
      parallels: number;
      panels: number;
      yOffset: number;
      // String-level collapse: N parallel strings on this MPPT all
      // have the same panel count, drawn as one lane labelled "× N
      // strings". Set to the string count.
      collapsedCount?: number;
      // MPPT-level collapse: this lane represents M consecutive
      // MPPTs that are all configured identically (same parallels,
      // same panel count). Set to the MPPT count.
      mpptCollapsedCount?: number;
      // MPPT range when mpptCollapsedCount > 1, e.g. [1, 9] for
      // "MPPTs 1–9". When undefined the lane represents only
      // `mppt` (a single MPPT number).
      mpptRange?: [number, number];
    }>;
    invNodeId?: string;
    imsNodeId?: string;
  }> = [];

  macroInverters.forEach(inv => {
    // ---- Pre-compute each MPPT's "shape" ----
    // An MPPT is "uniform" if every string on it has the same panel
    // count. Uniform MPPTs reduce to a single (parallels, panels)
    // pair. Two uniform MPPTs are interchangeable if those pairs
    // match — meaning we can collapse them into one lane labelled
    // "MPPTs A–B".
    type MpptShape =
      | { kind: 'uniform'; mpptNumber: number; parallels: number; panels: number }
      | { kind: 'mixed';   mpptNumber: number; strings: { panels: number }[] };

    const shapes: MpptShape[] = inv.mppts.map(mppt => {
      const same = mppt.strings.length > 0
        && mppt.strings.every(s => s.panels === mppt.strings[0].panels);
      if (same) {
        return {
          kind: 'uniform',
          mpptNumber: mppt.mpptNumber,
          parallels: mppt.strings.length,
          panels: mppt.strings[0].panels
        };
      }
      return { kind: 'mixed', mpptNumber: mppt.mpptNumber, strings: mppt.strings };
    });

    // ---- Build lanes, grouping consecutive uniform MPPTs ----
    // Walk shapes; when we see a run of 2+ consecutive uniform MPPTs
    // with matching (parallels, panels), emit ONE collapsed lane
    // covering the whole run. Mixed MPPTs and one-off uniforms emit
    // their normal lanes (string-collapsed for parallels>1 uniforms,
    // per-string for mixed).
    const lanes: typeof invLayouts[number]['lanes'] = [];
    let i = 0;
    while (i < shapes.length) {
      const s = shapes[i];

      if (s.kind === 'uniform') {
        // How many consecutive shapes from here match this signature?
        let runEnd = i;
        while (
          runEnd + 1 < shapes.length
          && shapes[runEnd + 1].kind === 'uniform'
          && (shapes[runEnd + 1] as Extract<MpptShape, {kind:'uniform'}>).parallels === s.parallels
          && (shapes[runEnd + 1] as Extract<MpptShape, {kind:'uniform'}>).panels    === s.panels
        ) runEnd++;

        const runLength = runEnd - i + 1;
        const lastMppt  = (shapes[runEnd] as Extract<MpptShape, {kind:'uniform'}>).mpptNumber;

        if (runLength > 1) {
          // Collapsed: one lane represents all MPPTs in the run.
          // Use parallels>1 only when the underlying strings really
          // were parallel; collapsedCount on its own (without mppt
          // collapse) would say "× N strings" — here we want both
          // annotations available.
          lanes.push({
            mppt: s.mpptNumber,
            stringIdx: 0,
            parallels: s.parallels,
            panels: s.panels,
            yOffset: lanes.length * L.laneHeight + L.laneHeight / 2,
            collapsedCount: s.parallels > 1 ? s.parallels : undefined,
            mpptCollapsedCount: runLength,
            mpptRange: [s.mpptNumber, lastMppt]
          });
        } else {
          // Single uniform MPPT — string-collapse if parallels>1.
          lanes.push({
            mppt: s.mpptNumber,
            stringIdx: 0,
            parallels: s.parallels,
            panels: s.panels,
            yOffset: lanes.length * L.laneHeight + L.laneHeight / 2,
            collapsedCount: s.parallels > 1 ? s.parallels : undefined
          });
        }
        i = runEnd + 1;
      } else {
        // Mixed MPPT — render every string as its own lane.
        s.strings.forEach((str, sIdx) => {
          lanes.push({
            mppt: s.mpptNumber,
            stringIdx: sIdx,
            parallels: s.strings.length,
            panels: str.panels,
            yOffset: lanes.length * L.laneHeight + L.laneHeight / 2
          });
        });
        i++;
      }
    }

    const h = Math.max(lanes.length * L.laneHeight, L.invH);
    invLayouts.push({ inv, y: cursorY, h, lanes });
    cursorY += h + L.invVSpacing;
  });

  // Microinverter blocks — one per identical group. Each is a single
  // compact AC-branch lane (Pass 2 renders the fleet glyph + one
  // collapsed AC-module string), not a per-unit inverter train.
  microGroups.forEach((mg) => {
    const h = Math.max(L.laneHeight, L.invH);
    invLayouts.push({ microGroup: mg, y: cursorY, h, lanes: [] });
    cursorY += h + L.invVSpacing;
  });

  // AC-coupled batteries append as separate blocks. Identical units
  // (same model + manufacturer + backup + isExisting + inverter spec)
  // collapse into a single row tagged with "× N", same as the panel-
  // string MPPT collapse — modular battery products like the Enphase
  // IQ Battery 5P routinely ship in stacks of 4–6 to hit a target
  // capacity, and rendering each one as its own row eats the entire
  // page for no extra information. The topology stays as N separate
  // Battery objects (each has its own id, so user-level operations
  // like "mark this one EXISTING" still work via the editor); we
  // just visualise them together.
  const acBatteries = t.batteries.filter(b => b.coupling === 'ac');
  const batterySig = (b: Topology['batteries'][number]): string =>
    [
      // Stack label first: batteries with different stack/BMS labels
      // must NEVER merge, even when otherwise identical — that's how a
      // user splits one pile of modules into multiple stacks, each with
      // its own battery inverter / BMS block.
      b.stackLabel ?? '',
      b.manufacturer, b.model, b.capacity, b.backupGateway ? 'bk' : 'no',
      b.isExisting ? 'ex' : 'new',
      b.inverter
        ? `${b.inverter.manufacturer}|${b.inverter.model}|${b.inverter.capacity}|${b.inverter.phases}`
        : '',
    ].join('::');
  // Group by signature (which now includes the stack/BMS label), keyed
  // so identical same-stack modules merge into one block regardless of
  // their order in the array. Different stack labels — or genuinely
  // different battery types — get their own block, so a user splits one
  // pile of modules into N stacks simply by labelling them.
  const batteryGroupMap = new Map<string, { rep: typeof acBatteries[number]; count: number }>();
  const batteryGroups: Array<{ rep: typeof acBatteries[number]; count: number }> = [];
  for (const bat of acBatteries) {
    const key = batterySig(bat);
    const existing = batteryGroupMap.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      const grp = { rep: bat, count: 1 };
      batteryGroupMap.set(key, grp);
      batteryGroups.push(grp);   // preserve first-seen order
    }
  }
  batteryGroups.forEach(({ rep, count }) => {
    const h = L.invH * 0.85;
    invLayouts.push({
      battery: rep,
      batteryStackCount: count,
      y: cursorY,
      h,
      lanes: [],
    });
    cursorY += h + L.invVSpacing;
  });

  // ----- Multi-inverter detection -----
  // When more than one "inverter block" stacks vertically, single-column AC
  // drop routing breaks down (see notes around Pass 3). For these layouts
  // we override `ac_out` to exit on the LEFT of the inverter via
  // customTerminals, and place IMSes to the LEFT of each inverter — so the
  // AC bus runs down a clear left-side channel rather than through other
  // inverters' bodies.
  const totalInvs = invLayouts.length;
  const isMulti = totalInvs > 1;

  // Helper: build the customTerminals override that moves ac_out to the LEFT
  // of an inverter / hybrid inverter symbol. Returns undefined when not in
  // multi-inverter mode (single-inverter layouts keep the original bottom-
  // centre ac_out so the AC chain can drop straight down beneath the
  // inverter — looks fine when there's only one of them).
  function multiInvCustomTerminals(symId: 'inverter' | 'hybridInverter'):
      SldNode['data']['customTerminals'] | undefined {
    if (!isMulti) return undefined;
    const sym = SYMBOLS[symId];
    const W = sym.nativeBox.w;
    const H = sym.nativeBox.h;
    const base: NonNullable<SldNode['data']['customTerminals']> = {
      // dc_in stays on the right edge (DC side faces panels which are
      // drawn to the right).
      dc_in:  { id: 'dc_in',  x: W, y: H / 2, type: 'dc' },
      // ac_out moves from bottom-centre to LEFT-centre. After the inverter
      // glyph's horizontal mirror the visible "AC" label is already on the
      // left, so this also makes terminal placement match the visual.
      ac_out: { id: 'ac_out', x: 0, y: H / 2, type: 'ac' }
    };
    if (symId === 'hybridInverter') {
      base.bat_port = { id: 'bat_port', x: W, y: H * 0.75, type: 'dc' };
    }
    return base;
  }

  // ----- Pass 2: place inverters + DC side for each -----
  invLayouts.forEach((il, ilIdx) => {
    if (il.battery) {
      // AC-coupled battery inverter (one per stack — represents the
      // shared inverter for all N modules in the stack).
      const bat = il.battery;
      const stack = il.batteryStackCount ?? 1;
      const totalKwh = bat.capacity * stack;

      // Inverter capacity is per-unit; for stacks we surface the
      // aggregate so the spec line tracks total system AC out.
      const invKw = bat.inverter ? bat.inverter.capacity / 1000 : 0;
      const invSubline = bat.inverter
        ? stack > 1
          ? `${(invKw * stack).toFixed(1)} kW · ${bat.inverter.phases}φ · ${stack}× ${invKw.toFixed(1)} kW`
          : `${invKw.toFixed(1)} kW · ${bat.inverter.phases}φ`
        : '';
      // For the embedded-inverter (battery's own AC↔DC) we override
      // terminals to DROP `dc_in`. There's no PV input here — the
      // battery is the only thing on the DC side — so showing an
      // unused dc_in dot just makes users wonder which DC port the
      // wire is hitting. Keep ac_out (to AC bus) and bat_port (to
      // the battery), with bat_port repositioned to the right-centre
      // since it's now the only DC terminal.
      const hSym = SYMBOLS.hybridInverter;
      const hW = hSym.nativeBox.w;
      const hH = hSym.nativeBox.h;
      // Reuse the same multi-inverter detection used elsewhere in the
      // pass — defined just above on line ~678.
      const invId = addNode(ctx, 'hybridInverter', L.invX, il.y, {
        label: bat.inverter?.model || 'Battery Inverter',
        sublabel: invSubline,
        topologyRef: { kind: 'battery', id: bat.id },
        customTerminals: {
          // ac_out: matches the multi-inverter override (left-centre)
          // when in multi mode, bottom-centre native otherwise.
          ac_out: isMulti
            ? { id: 'ac_out', x: 0, y: hH / 2, type: 'ac' }
            : { id: 'ac_out', x: hW / 2, y: hH, type: 'ac' },
          // bat_port: right-centre. The native position (75% down)
          // looks oddly low when there's nothing above it.
          bat_port: { id: 'bat_port', x: hW, y: hH / 2, type: 'dc' },
          // Note: dc_in deliberately omitted — see comment above.
        },
      });
      il.invNodeId = invId;

      // Battery to the right. For a stack we render an offset-shadow
      // visual hint (1–2 lighter copies behind the front face) so the
      // viewer immediately sees "this is a stack" before reading the
      // "× N" label. Done via a customContent override.
      const batX = L.invX + L.invW + 60;
      const batY = il.y + il.h / 2 - 14;
      const stackBadge = stack > 1 ? ` × ${stack}` : '';
      const subline =
        stack > 1
          ? `AC-coupled${bat.backupGateway ? ' · backup' : ''} · ${bat.capacity} kWh each · ${totalKwh} kWh total`
          : `AC-coupled${bat.backupGateway ? ' · backup' : ''} · ${bat.capacity} kWh`;

      const batId = addNode(ctx, 'battery', batX, batY, {
        label: `${bat.model}${stackBadge}`,
        sublabel: subline,
        topologyRef: { kind: 'battery', id: bat.id },
        isExisting: bat.isExisting === true,
        // For a stack, override the symbol's content with a stacked-
        // shadow rendering. Two ghost rectangles behind the front face
        // visually communicate "multiple units" at a glance.
        customContent: stack > 1 ? buildStackedBatteryContent(stack) : undefined,
      });
      // Wire battery to inverter battery port
      addEdge(ctx, invId, 'bat_port', batId, 'pos', 'dc+');
      return;
    }

    if (il.microGroup) {
      // ----- Microinverter fleet — ONE compact AC-branch block -----
      const { rep, count, totalPanels } = il.microGroup;
      const panelType = getInverterPanelType(t, rep);
      const totalAcKw = (rep.capacity * count) / 1000;

      // Single inverter glyph stands in for the whole fleet. ac_out
      // exits left in multi-block layouts (shared AC bus channel), or
      // bottom-centre when it's the only block.
      const mInvId = addNode(ctx, 'inverter', L.invX, il.y, {
        label: `${rep.model} × ${count}`,
        sublabel: `${totalAcKw.toFixed(2)} kW AC · ${rep.phases}φ · microinverters`,
        topologyRef: { kind: 'inverter', id: rep.id },
        customTerminals: multiInvCustomTerminals('inverter'),
        isExisting: rep.isExisting === true,
      });
      il.invNodeId = mInvId;

      // One collapsed AC-module string to the right. No rooftop DC
      // isolator / string fuse — micros are AC modules, the DC side is
      // internal to each unit. "× N panels" communicates the array size.
      const labelLine = panelType
        ? `AC modules · ${rep.model} · ${panelType.model}`
        : `AC modules · ${rep.model}`;
      const stringRender = renderPanelString(
        totalPanels,
        labelLine,
        undefined,
        panelType?.isExisting === true,
      );
      // Align the string's `pos` terminal Y with the inverter dc_in Y.
      const mInvSym = SYMBOLS.inverter;
      const dcInAbsY = il.y + (mInvSym.nativeBox.h / 2) * mInvSym.defaultScale;
      const mStringId = newId(ctx, 'panelString');
      const mPanelX = L.invX + L.invW + L.dxRoofIso;
      ctx.nodes.push({
        id: mStringId,
        type: 'sldSymbol',
        position: { x: mPanelX, y: dcInAbsY - stringRender.terminals.pos.y },
        data: {
          symbolId: 'panelString',
          customContent: stringRender.content,
          customBox: { w: stringRender.nativeBox.w, h: stringRender.nativeBox.h },
          customTerminals: {
            pos: { id: 'pos', x: stringRender.terminals.pos.x, y: stringRender.terminals.pos.y, type: 'dc+' },
            neg: { id: 'neg', x: stringRender.terminals.neg.x, y: stringRender.terminals.neg.y, type: 'dc-' },
          },
          rotation: 0,
          topologyRef: { kind: 'inverter', id: rep.id },
          isExisting: panelType?.isExisting === true,
        },
      });
      addEdge(ctx, mInvId, 'dc_in', mStringId, 'pos', 'dc+');
      return;
    }

    const inv = il.inv!;
    const invSymId = inv.type === 'hybrid' ? 'hybridInverter' : 'inverter';
    const invId = addNode(ctx, invSymId, L.invX, il.y, {
      label: inv.model,
      sublabel: `${(inv.capacity / 1000).toFixed(1)} kW · ${inv.phases}φ`,
      topologyRef: { kind: 'inverter', id: inv.id },
      customTerminals: multiInvCustomTerminals(invSymId),
      isExisting: inv.isExisting === true,
    });
    il.invNodeId = invId;

    // DC side per lane
    il.lanes.forEach(lane => {
      const laneY = il.y + lane.yOffset;
      const invRight = L.invX + L.invW;

      // Rooftop DC isolator
      const rcvY = laneY - 12;
      const rooftopId = addNode(ctx, 'dcIsolator', invRight + L.dxRoofIso - 15, rcvY);

      // Wire inverter.dc_in (top-middle) → rooftopIsolator.a.
      // Note: the inverter body's dc_in is at body top-center. This is a
      // visual shorthand for "DC side", wires physically enter on the right.
      addEdge(ctx, invId, 'dc_in', rooftopId, 'a', 'dc+');

      // Fuse (if parallel) — horizontal cartridge that sits inline on the bus.
      let lastNodeId = rooftopId;
      let lastTerm = 'b';
      if (lane.parallels > 1) {
        const fuseSym = SYMBOLS.fuse;
        const fuseId = addNode(
          ctx,
          'fuse',
          invRight + L.dxFuse - fuseSym.nativeBox.w / 2,
          laneY - fuseSym.nativeBox.h / 2
        );
        addEdge(ctx, lastNodeId, lastTerm, fuseId, 'a', 'dc+');
        lastNodeId = fuseId;
        lastTerm = 'b';
      }

      // Panel string — rendered as a single composite node showing either all
      // panels (≤4) or a condensed "[P] [P] ··· [P] × N" form (5+).
      let panelX = invRight + L.dxPanels;
      const hasOpt = inv.optimisers === true;
      if (hasOpt) {
        const optId = addNode(ctx, 'optimiser', panelX, laneY - 4.5);
        addEdge(ctx, lastNodeId, lastTerm, optId, 'a', 'dc+');
        lastNodeId = optId;
        lastTerm = 'b';
        panelX += 24;
      }

      // Create a single composite "panel string" node with count = lane.panels.
      // The label is rendered INSIDE the composite (above the rails) so we
      // don't pass `label` to React Flow data — that would double-render it
      // below the symbol.
      // Five label shapes (in priority order — most specific first):
      //   - mppt+string collapsed:  "MPPTs 1–9 · × 2 strings each"
      //   - mppt collapsed only:    "MPPTs 1–9"
      //   - string collapsed only:  "MPPT 2 · × 4 strings"
      //   - mixed parallels:        "String 2 of 3 · MPPT 1"
      //   - single:                 "MPPT 1"
      // Surface the panel model in the lane label so a per-block panel-type
      // change is immediately visible above the rails (otherwise only the
      // bottom title block reflects the change — easy to miss).
      const lanePanelType = getInverterPanelType(t, inv);
      const modelSuffix = lanePanelType ? ` · ${lanePanelType.model}` : '';

      let stringLabel: string;
      if (lane.mpptRange) {
        const [a, b] = lane.mpptRange;
        const mpptPart = `MPPTs ${a}–${b}`;
        stringLabel = lane.collapsedCount
          ? `${mpptPart} · × ${lane.collapsedCount} strings each${modelSuffix}`
          : `${mpptPart}${modelSuffix}`;
      } else if (lane.collapsedCount) {
        stringLabel = `MPPT ${lane.mppt} · × ${lane.collapsedCount} strings${modelSuffix}`;
      } else if (lane.parallels > 1) {
        stringLabel = `String ${lane.stringIdx + 1} of ${lane.parallels} · MPPT ${lane.mppt}${modelSuffix}`;
      } else {
        stringLabel = `MPPT ${lane.mppt}${modelSuffix}`;
      }
      // Total panels this lane actually represents — used to render a
      // "S {total}" badge inside the composite for collapsed lanes so the
      // reader can see the true panel count at a glance, not just the
      // per-string number. For non-collapsed lanes the total === count
      // and the badge is suppressed.
      const stringMult = lane.collapsedCount       ?? 1;
      const mpptMult   = lane.mpptCollapsedCount   ?? 1;
      const totalPanelsForLane = lane.panels * stringMult * mpptMult;
      const stringRender = renderPanelString(
        lane.panels,
        stringLabel,
        totalPanelsForLane,
        lanePanelType?.isExisting === true,
      );
      const stringNodeId = newId(ctx, 'panelString');
      // Position the composite so its `pos` terminal Y aligns exactly with
      // laneY (composite.position.y + pos.y = laneY  →  position.y = laneY - pos.y).
      // pos terminal sits AT the top-rail level so the incoming DC+ wire lands
      // in the rail level with no vertical jog. customTerminals is passed so
      // React Flow handles use the dynamic positions (top rail, bottom rail)
      // rather than the static y=15 placeholder in the symbol library.
      ctx.nodes.push({
        id: stringNodeId,
        type: 'sldSymbol',
        position: {
          x: panelX,
          y: laneY - stringRender.terminals.pos.y
        },
        data: {
          symbolId: 'panelString',
          customContent: stringRender.content,
          customBox: { w: stringRender.nativeBox.w, h: stringRender.nativeBox.h },
          customTerminals: {
            pos: { id: 'pos', x: stringRender.terminals.pos.x, y: stringRender.terminals.pos.y, type: 'dc+' },
            neg: { id: 'neg', x: stringRender.terminals.neg.x, y: stringRender.terminals.neg.y, type: 'dc-' }
          },
          rotation: 0,
          // Tag with the owning inverter so PropertiesPanel's PanelEditor
          // can scope its model-swap to "this inverter's panel type"
          // rather than the topology-global panels field.
          topologyRef: { kind: 'inverter', id: inv.id },
          // Inherit EXISTING from the resolved panel type so the
          // SldSymbolNode wrapper draws the dashed boundary + badge.
          isExisting: lanePanelType?.isExisting === true,
        }
      });
      // Wire previous node (fuse or rooftop iso) → string.pos
      addEdge(ctx, lastNodeId, lastTerm, stringNodeId, 'pos', 'dc+');
    });

    // DC-coupled battery
    const dcBatt = t.batteries.find(b => b.coupling === 'dc' && b.connectedTo === inv.id);
    if (dcBatt) {
      const batX = L.invX + L.invW + 30;
      const batY = il.y + il.h + 8;
      const batId = addNode(ctx, 'battery', batX, batY, {
        label: dcBatt.model,
        sublabel: `DC-coupled · ${dcBatt.capacity} kWh`,
        topologyRef: { kind: 'battery', id: dcBatt.id },
        isExisting: dcBatt.isExisting === true,
      });
      addEdge(ctx, invId, 'bat_port', batId, 'pos', 'dc+');
    }
  });

  // ----- Pass 3: AC side — inverters → bus → MSB → grid -----
  const lastInv = invLayouts[invLayouts.length - 1];
  const busY = (lastInv ? lastInv.y + lastInv.h : L.topMargin) + L.busYOffset;
  const msbY = busY + L.msbYOffset;

  // ---- Place IMSes (one per inverter) ----
  if (isMulti) {
    // Multi-inverter: place each IMS to the LEFT of its inverter, in the
    // same Y row. The inverter's ac_out is overridden to the LEFT side
    // (see multiInvCustomTerminals in Pass 2), so the inverter→IMS wire
    // is a short horizontal stub. This eliminates the original problem
    // where a single AC drop column at x≈240 had to traverse the bodies
    // and rooftop DC isolators of every inverter beneath it.
    const imsX = L.invX - 50;       // IMS box left edge — sits ~24px left of inverter
    // Compute the actual rendered Y of the inverter's `ac_out` terminal
    // so the IMS lines up with it, not with L.invH (the layout's nominal
    // inverter height — which differs from the symbol's real scaled
    // height by ~25 px and was causing a visible S-bend in the wire
    // between IMS.b and inverter.ac_out).
    const invSym = SYMBOLS.inverter;
    const invHybridSym = SYMBOLS.hybridInverter;
    // Multi-inverter mode overrides ac_out to (0, H/2) — see
    // multiInvCustomTerminals. Both inverter + hybridInverter have the
    // same UPB DC_AC base dimensions, so a single Y suffices.
    const invAcOutNativeY = invSym.nativeBox.h / 2;
    const invScale = invSym.defaultScale;
    const invAcOutOffsetY = invAcOutNativeY * invScale;
    // BMS rows use the hybridInverter symbol — same dimensions in
    // practice but read from the correct symbol so future changes to
    // either don't silently break alignment.
    const invHybridAcOutOffsetY =
      (invHybridSym.nativeBox.h / 2) * invHybridSym.defaultScale;
    const imsSym = SYMBOLS.acIsolator;
    const imsTermBNativeY = imsSym.terminals.b.y;
    const imsScale = imsSym.defaultScale;
    const imsTermBOffsetY = imsTermBNativeY * imsScale;

    invLayouts.forEach((il, idx) => {
      if (!il.invNodeId) return;
      // Place the IMS so its `b` terminal sits at the same absolute Y
      // as the inverter's ac_out terminal. The wire then has the same
      // sourceY and targetY → SldWire's straight-path branch kicks in
      // and the S-snake disappears.
      const acOutAbsY =
        il.y + (il.battery ? invHybridAcOutOffsetY : invAcOutOffsetY);
      const imsY = acOutAbsY - imsTermBOffsetY;
      const imsId = addNode(ctx, 'acIsolator', imsX, imsY, {
        label: il.battery ? (il.battery.stackLabel || 'BMS') : `IMS ${idx + 1}`
      });
      // Inverter.ac_out (left of inverter) → IMS.b (right of IMS, faces inverter)
      addEdge(ctx, il.invNodeId, 'ac_out', imsId, 'b', 'ac');
      il.imsNodeId = imsId;
    });
  } else {
    // Single inverter: IMS sits inline at the start of the horizontal MSB
    // chain — an unbroken row reads cleanly when there's nothing to merge.
    const imsX = L.invX + L.invW * 0.35;
    invLayouts.forEach((il) => {
      if (!il.invNodeId) return;
      const imsY = busY - 30;
      const imsId = addNode(ctx, 'acIsolator', imsX - 13, imsY, {
        label: il.battery ? (il.battery.stackLabel || 'BMS') : 'IMS'
      });
      addEdge(ctx, il.invNodeId, 'ac_out', imsId, 'a', 'ac');
      il.imsNodeId = imsId;
    });
  }

  // ---- MSB chain: AC isolator → main switch → [CT] → meter → service fuse → utility meter → TX → grid ----
  // Multi-inverter: the AC isolator sits in a column to the LEFT of the
  // IMSes, forming the foot of the vertical AC bus. Each IMS's `a`
  // (LEFT) terminal wires to AC iso's `a` (also LEFT); smooth-step
  // routing collapses these drops into a single visible vertical bus
  // segment, and the 3+ wire convergence on AC iso.a draws a junction
  // dot via SldSymbolNode's wireCount logic — making the bus->iso
  // connection point unambiguous.
  // Single inverter: AC iso sits inline with the IMS at the start of the
  // horizontal MSB chain (original layout).
  const busTapX = isMulti
    ? L.invX - 100   // bus column on the far left, well clear of inverters
    : L.invX + L.invW * 0.35;
  let mx = busTapX;

  const acIsoId = addNode(ctx, 'acIsolator', mx - 13, msbY - 10, { label: 'AC ISOLATOR' });

  // Connect every IMS to the AC ISOLATOR. Multi-inverter uses IMS.a (left)
  // so the wires drop down the bus column; single-inverter uses IMS.b
  // (right) for a short inline join.
  invLayouts.forEach((il) => {
    if (il.imsNodeId) {
      addEdge(ctx, il.imsNodeId, isMulti ? 'a' : 'b', acIsoId, 'a', 'ac');
    }
  });

  // ---- Sub-board insert (when solar wires through SDB instead of MSB) ----
  // Standard SLD convention: when solar's AC isolator feeds a sub-board
  // rather than the main board directly, an extra "SDB MAIN SWITCH" block
  // sits between the solar AC iso and the main switchboard. This makes
  // the additional switching point explicit (required by AS/NZS 3000 +
  // 4777.1 as a separately-isolatable feed for the inverter circuit).
  let chainHeadId = acIsoId;
  let chainHeadTerm = 'b';
  const sdbActive = (t.switchgear.solarConnectionPoint === 'sub')
                    && (t.switchgear.subBoard?.present === true);
  if (sdbActive) {
    mx += 95;
    const sdbLabel = t.switchgear.subBoard?.label
      ? `${t.switchgear.subBoard.label} MS`
      : 'SDB MAIN SWITCH';
    const sdbId = addNode(ctx, 'acIsolator', mx - 13, msbY - 10, { label: sdbLabel });
    addEdge(ctx, chainHeadId, chainHeadTerm, sdbId, 'a', 'ac');
    chainHeadId = sdbId;
    chainHeadTerm = 'b';
  }

  mx += 95;
  const mainSwId = addNode(ctx, 'acIsolator', mx - 13, msbY - 10, { label: 'MAIN SWITCH' });
  addEdge(ctx, chainHeadId, chainHeadTerm, mainSwId, 'a', 'ac');

  let prevId = mainSwId;
  let prevTerm = 'b';

  if (t.switchgear.ctMetering) {
    mx += 75;
    const ctSym = SYMBOLS.ammeter;
    const ctH = ctSym.nativeBox.h * ctSym.defaultScale;
    const ctId = addNode(ctx, 'ammeter', mx - (ctSym.nativeBox.w * ctSym.defaultScale) / 2, msbY - ctH / 2, { label: 'CT' });
    addEdge(ctx, prevId, prevTerm, ctId, 'a', 'ac');
    prevId = ctId;
    prevTerm = 'b';
  }

  mx += 80;
  const meterId = addNode(ctx, 'meter', mx - 12, msbY - 12, { label: 'ENERGY METER' });
  addEdge(ctx, prevId, prevTerm, meterId, 'a', 'ac');

  mx += 100;
  const sfuseId = addNode(ctx, 'serviceFuse', mx - 11, msbY - 5, { label: 'SERVICE FUSE' });
  addEdge(ctx, meterId, 'b', sfuseId, 'a', 'ac');

  mx += 95;
  const utilMeterId = addNode(ctx, 'meter', mx - 12, msbY - 12, { label: 'UTILITY METER' });
  addEdge(ctx, sfuseId, 'b', utilMeterId, 'a', 'ac');

  mx += 95;
  const txId = addNode(ctx, 'transformer', mx - 11, msbY - 7, { label: 'TX' });
  addEdge(ctx, utilMeterId, 'b', txId, 'a', 'ac');

  mx += 70;
  const gridSym = SYMBOLS.grid;
  const gridH = gridSym.nativeBox.h * gridSym.defaultScale;
  const gridId = addNode(ctx, 'grid', mx - (gridSym.nativeBox.w * gridSym.defaultScale) / 2, msbY - gridH / 2, {
    label: `${t.grid.phases}φ GRID`
  });
  addEdge(ctx, txId, 'b', gridId, 'ac', 'ac');

    // ----- Title block (bottom-right of page) -----
    ctx.nodes.push(makeTitleBlockNode(t));

    return { nodes: ctx.nodes, edges: ctx.edges };
  } finally {
    Object.assign(L, savedL);
  }
}
